import React, { useEffect, useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchDeletedBooks, restoreBook, hardDeleteBook } from '../../store/books/booksSlice';
import { Button, Modal, TableRowSkeleton, EmptyState, useToast } from '../../components/common';
import { Book } from '../../types';

export const DeletedBooksPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { deletedBooks, isLoading } = useAppSelector((state) => state.books);
  const [deleteModal, setDeleteModal] = useState<Book | null>(null);

  useEffect(() => {
    dispatch(fetchDeletedBooks());
  }, [dispatch]);

  const handleRestore = async (id: string, title: string) => {
    try {
      await dispatch(restoreBook(id)).unwrap();
      showToast(`"${title}" has been restored`, 'success');
    } catch {
      showToast('Failed to restore book', 'error');
    }
  };

  const handleHardDelete = async () => {
    if (deleteModal) {
      try {
        await dispatch(hardDeleteBook(deleteModal._id)).unwrap();
        showToast(`"${deleteModal.title}" has been permanently deleted`, 'success');
      } catch {
        showToast('Failed to delete book', 'error');
      }
      setDeleteModal(null);
    }
  };

  return (
    <div>
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Deleted Books</h1>
        <p className="text-sm sm:text-base text-slate-500">Restore or permanently delete books</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Cover</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Author</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Deleted At</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} columns={5} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : deletedBooks.length === 0 ? (
        <EmptyState
          variant="deleted"
          title="No deleted books"
          description="Books you delete will appear here. You can restore them or permanently delete them."
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Cover</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Author</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Deleted At</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {deletedBooks.map((book) => (
                  <tr key={book._id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="w-10 h-14 rounded overflow-hidden bg-slate-100">
                        {book.coverImage ? (
                          <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                            {book.title.charAt(0)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 max-w-xs truncate">{book.title}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{book.author}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-[150px] truncate">
                      {book.deletedAt
                        ? new Date(book.deletedAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          onClick={() => handleRestore(book._id, book.title)}
                          title="Restore"
                        >
                          <RotateCcw size={16} />
                        </button>
                        <button
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          onClick={() => setDeleteModal(book)}
                          title="Delete Permanently"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Permanently Delete Book"
        size="sm"
      >
        <p className="text-slate-700">Are you sure you want to permanently delete "{deleteModal?.title}"?</p>
        <p className="text-sm text-red-600 mt-2">
          This action cannot be undone. The book and all its files will be permanently removed.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={() => setDeleteModal(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleHardDelete}>
            Delete Permanently
          </Button>
        </div>
      </Modal>
    </div>
  );
};
